import { LoginError } from 'modules/api-client';
import { Captcha, Form, FormErrorMessage } from 'modules/common-ui';
import {
  EyeIcon,
  EyeOffIcon,
  FormField,
  Link,
  TextInput,
} from 'modules/design-system';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { LinkWrapper } from '../../containers/index.css';

export type Credentials = {
  email: string;
  password: string;
  authenticationChallenge: string;
};

type Props = {
  defaultEmail: string;
  userExists: boolean;
  source?: string | null;
  login: (credentials: Credentials) => Promise<void>;
  withResetPasswordLink?: boolean;
};

export const PasswordSignInForm = ({
  defaultEmail,
  userExists: _userExists,
  source: _source,
  login,
  withResetPasswordLink = true,
}: Props) => {
  const theme = useTheme();
  const [t] = useTranslation('user');
  const [form, setForm] = useState({
    email: defaultEmail,
    password: '',
    authenticationChallenge: '',
  });
  const [
    requireExtraAuthenticationChallenge,
    setRequireExtraAuthenticationChallenge,
  ] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const submitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.email || !form.password || pending) return;

    setPending(true);
    try {
      await login(form);
    } catch (err) {
      setPending(false);
      if (
        err instanceof LoginError &&
        err.requireExtraAuthenticationChallenge
      ) {
        setError(t('user:user.login.extraChallengeError'));
        setRequireExtraAuthenticationChallenge(true);
        return;
      }
      setError(
        err instanceof LoginError
          ? t('user.login.error401')
          : (err as Error).message || t('user.login.error'),
      );
    }
  };

  return (
    <Form
      onSubmit={submitForm}
      actions={{
        submit: {
          label: pending
            ? t('user.login.form.submit.loading')
            : t('user.login.form.submit.label'),
          variant: 'primary',
          buttonProps: {
            block: true,
            disabled:
              !form.email ||
              !form.password ||
              (requireExtraAuthenticationChallenge &&
                !form.authenticationChallenge) ||
              pending,
          },
        },
      }}
    >
      <FormField
        label={t('user.login.form.username.label')}
        htmlFor="login-username"
      >
        <TextInput
          id="login-username"
          name="email"
          readOnly={Boolean(defaultEmail)}
          value={form.email}
          placeholder={t('user.login.form.username.placeholder')}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          autoFocus
          fullWidth
          type="email"
          autoComplete="username"
        />
      </FormField>

      <FormField
        label={t('user.login.form.password.label')}
        htmlFor="login-password"
      >
        <TextInput
          id="login-password"
          name="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder={t('user.login.form.password.placeholder')}
          required
          fullWidth
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          icon={{
            component: showPassword ? (
              <EyeOffIcon color={theme.colors.icon.subtle} />
            ) : (
              <EyeIcon />
            ),
            position: 'right',
            onClick: () => setShowPassword(!showPassword),
          }}
        />
      </FormField>

      {requireExtraAuthenticationChallenge && (
        <Captcha
          onSuccess={(token) =>
            setForm({ ...form, authenticationChallenge: token })
          }
          onExpire={() => setForm({ ...form, authenticationChallenge: '' })}
        />
      )}

      {error && (
        <FormErrorMessage style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
          {error}
        </FormErrorMessage>
      )}

      {withResetPasswordLink && (
        <LinkWrapper>
          <Link
            to={`/reset-password?${new URLSearchParams({ email: form.email })}`}
          >
            {t('user.login.resetPassword')}
          </Link>
        </LinkWrapper>
      )}
    </Form>
  );
};
